import { FreeformResponse } from "./freeform.model";
import { PollResponse } from "./poll.model";
import { ENTITY_PREFIX, FreeformId, PollId } from "../../shared/models/entity.model";

export type Interactive = PollResponse | FreeformResponse;

export type InteractiveType = "freeform" | "poll";
export type InteractiveState = "DRAFT" | "PUBLISHED" | "PAUSED" | "ARCHIVED";
export type TimeWindow = "NONE" | "START_ONLY" | "END_ONLY" | "START_AND_END";

export interface InteractiveBase<T extends InteractiveType> {
    type: T;
    id: string; // plain UUID
    tenantId: string; // plain UUID
    name: string;
    prompt: string | null;
    description: string | null;
    state: InteractiveState;
    timeWindow: TimeWindow;
    startDate: string | null; // ISO 8601
    endDate: string | null; // ISO 8601
    createdAt: string; // ISO 8601
    updatedAt: string; // ISO 8601
}

export function toInteractiveTypedId(interactive: Interactive): PollId | FreeformId {
    const prefix = interactive.type === "poll" ? ENTITY_PREFIX.INTERACTIVE_POLL : ENTITY_PREFIX.INTERACTIVE_FREEFORM;
    return `${prefix}:${interactive.id}`;
}

export function requiresStart(timeWindow: TimeWindow): boolean {
    return timeWindow === "START_ONLY" || timeWindow === "START_AND_END";
}

export function requiresEnd(timeWindow: TimeWindow): boolean {
    return timeWindow === "END_ONLY" || timeWindow === "START_AND_END";
}

export interface InteractiveSchedule {
    state: InteractiveState;
    timeWindow: TimeWindow;
    startDate: string | null;
    endDate: string | null;
}

export function isScheduled(interactive: InteractiveSchedule): boolean {
    return (
        interactive.state === "PUBLISHED" &&
        requiresStart(interactive.timeWindow) &&
        interactive.startDate !== null &&
        new Date(interactive.startDate).getTime() > Date.now()
    );
}

export function isLive(interactive: InteractiveSchedule): boolean {
    const now = Date.now();
    return (
        interactive.state === "PUBLISHED" &&
        (!requiresStart(interactive.timeWindow) ||
            (interactive.startDate !== null && new Date(interactive.startDate).getTime() <= now)) &&
        (!requiresEnd(interactive.timeWindow) ||
            (interactive.endDate !== null && new Date(interactive.endDate).getTime() > now))
    );
}

export function isExpired(interactive: InteractiveSchedule): boolean {
    return (
        interactive.state === "PUBLISHED" &&
        requiresEnd(interactive.timeWindow) &&
        interactive.endDate !== null &&
        new Date(interactive.endDate).getTime() <= Date.now()
    );
}

export type InteractiveDisplayState = InteractiveState | "LIVE" | "SCHEDULED" | "CLOSED";
export function getInteractiveDisplayState(interactive: InteractiveSchedule): InteractiveDisplayState {
    if (interactive.state === "PUBLISHED") {
        if (isLive(interactive)) return "LIVE";
        if (isScheduled(interactive)) return "SCHEDULED";
        if (isExpired(interactive)) return "CLOSED";
    }
    return interactive.state;
}
